import { GridCellParams } from '@mui/x-data-grid';
import { t } from 'i18next';
import useFlags from 'launchdarkly-react-client-sdk/lib/useFlags';
import { debounce } from 'lodash';
import { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useStrategicImperativeListConfig from './StrategicImperativeList.config';
import { PAGINATION_PAGE_SIZE } from 'basics/constants/common.constant';
import {
  mayaCustomerInitiativeCreateRoute,
  mayaSiViewRoute,
  mayaStrategicImperativeCreateRoute,
  mayaStrategicImperativeDuplicateRoute,
  mayaStrategicImperativeEditRoute,
} from 'basics/constants/routes.constants';
import { AllValidTherapeuticArea } from 'basics/enums/biogen.enums';
import useGetAllStrategicImperativeQuery from 'basics/graphql/queries/getAllStrategicImperatives';
import { SelectOption } from 'basics/options/options.types';
import { useCurrentUserState } from 'contexts/user';
import { SortFilter, StrategicImperative } from 'generated/maya.types';
import { useQueryParams } from 'hooks/useQueryParams';
import { useYodaCreateForm } from 'yoda-ui/yodaForm';

const useStrategicImperativeList = () => {
  const navigate = useNavigate();
  const flags = useFlags();
  const [siSortFilter, setSiSortFilter] = useState(SortFilter.NEWEST);
  const [siTitleFilter, setSiTitleFilter] = useState<(string)>();
  const [siTherapeuticAreaFilter, setSiTherapeuticAreaFilter] = useState<string[]>([]);
  const [strategicImperatives, setStrategicImperatives] = useState<StrategicImperative[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<{
    therapeuticArea: SelectOption[];
  }>({
    therapeuticArea: [],
  });
  const hasActiveFilters = Object.values(selectedFilters).some((options) => options.length > 0);
  const { setValue, providerFields } = useYodaCreateForm();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGINATION_PAGE_SIZE);
  const [rowCountState, setRowCountState] = useState(0);
  const { year } = useQueryParams();
  const { user } = useCurrentUserState();

  const queryOptions = useMemo(
    () => ({
      limit: pageSize,
      offset: page * pageSize,
    }),
    [page, pageSize],
  );

  const queryFilters = useMemo(
    () => ({
      title: siTitleFilter,
      country: user?.country === 'CA' && flags.mayaAkma846EnableDisplaySiForSpecificCountry ? [user?.country] : undefined,
      therapeuticArea: siTherapeuticAreaFilter.length ? siTherapeuticAreaFilter : null,
      year: year ? parseInt(year, 10) : undefined,
    }),
    [siTitleFilter, user?.country, flags.mayaAkma846EnableDisplaySiForSpecificCountry, siTherapeuticAreaFilter, year],
  );

  const { data, loading, count } = useGetAllStrategicImperativeQuery(queryOptions, queryFilters, siSortFilter);

  useEffect(() => {
    setRowCountState((prevRowCountState) => (count !== undefined ? count : prevRowCountState));
  }, [count, setRowCountState]);

  useEffect(() => {
    if (!loading) {
      setStrategicImperatives(data);
    }
  }, [data, loading]);

  const newStrategicImperativeClick = useCallback(() => {
    navigate(mayaStrategicImperativeCreateRoute);
  }, [navigate]);

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const onPageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
  };

  const handleChangeSiTitle = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setSiTitleFilter(event.target.value);
  };

  const debouncedHandleChangeSiTitle = useMemo(
    () => debounce(handleChangeSiTitle, 750),
    [],
  );

  const handleChangeSiTherapeuticArea = useCallback((event: SyntheticEvent<Element, Event>, value: SelectOption[]) => {
    const values = value.map((element) => element.id as string);

    if (values.includes(AllValidTherapeuticArea.BBU)) {
      values.push(AllValidTherapeuticArea.BIOSIMS);
    }

    setSiTherapeuticAreaFilter(values);
    setSelectedFilters((prev) => ({ ...prev, therapeuticArea: value }));
  }, [setSiTherapeuticAreaFilter]);

  const editSiCallback = async (ciId: string) => {
    navigate(`${mayaStrategicImperativeEditRoute}?id=${ciId}`);
  };

  const duplicateSiCallback = async (ciId: string) => {
    navigate(`${mayaStrategicImperativeDuplicateRoute}?cepSourceId=${ciId}`);
  };

  const addChildAction = async (ciId: string) => {
    navigate(`${mayaCustomerInitiativeCreateRoute}?parentId=${ciId}`);
  };

  const {
    strategicImperativeListColumnConfig,
    siTitleFieldConfig,
    siTherapeuticAreaFieldConfig,
    siSortFieldConfig,
  } = useStrategicImperativeListConfig(
    editSiCallback,
    duplicateSiCallback,
    addChildAction,
  );

  const handleClick = (params: GridCellParams) => {
    if (params.field !== 'action') {
      navigate(`${mayaSiViewRoute}?id=${params.row._id}`);
    }
  };

  const strategicImperativeListTitle = t('maya_si_list_title', { year });

  const handleRemoveChip = useCallback((filterType: 'therapeuticArea' | 'product', idToRemove: string | number) => {
    if (filterType === 'therapeuticArea') {
      const newOptions = selectedFilters.therapeuticArea.filter((o) => o.id !== idToRemove);
      const values = newOptions.map((element) => element.id as string);

      if (values.includes(AllValidTherapeuticArea.BBU)) {
        values.push(AllValidTherapeuticArea.BIOSIMS);
      }

      setSiTherapeuticAreaFilter(values);
      setSelectedFilters((prev) => ({ ...prev, therapeuticArea: newOptions }));
      setValue(siTherapeuticAreaFieldConfig.name, newOptions);
    }
  }, [selectedFilters, setValue, siTherapeuticAreaFieldConfig.name]);

  const handleReset = useCallback(() => {
    setSiTherapeuticAreaFilter([]);
    setSelectedFilters({
      therapeuticArea: [],
    });
    setValue(siTherapeuticAreaFieldConfig.name, []);
  }, [setValue, siTherapeuticAreaFieldConfig.name]);

  const hasSelectedFilters = selectedFilters.therapeuticArea.length > 0;

  const handleSortChange = useCallback((value: SelectOption) => {
    setSiSortFilter(value.id as SortFilter);
  }, []);

  const menuStrategicImperativeLabel = t('maya_main_menu_strategic_imperative_label');

  const newElementButton = {
    newElementLabel: t('maya_list_new_element_label', { elementTitle: menuStrategicImperativeLabel }),
    newElementClick: newStrategicImperativeClick,
  };

  return {
    debouncedHandleChangeSiTitle,
    handleChangeSiTherapeuticArea,
    handleClick,
    loading,
    strategicImperativeListTitle,
    newElementButton,
    onPageChange,
    onPageSizeChange,
    rowCountState,
    siTitleFieldConfig,
    siTherapeuticAreaFieldConfig,
    strategicImperativeListColumnConfig,
    providerFields,
    strategicImperatives,
    selectedFilters,
    handleRemoveChip,
    handleReset,
    hasActiveFilters,
    siSortFieldConfig,
    handleSortChange,
    hasSelectedFilters,
    siSortFilter,
  };
};

export default useStrategicImperativeList;
